import {Dispatch, SetStateAction} from "react";
import {useToast} from "@chakra-ui/react";
import {useLocation, useNavigate} from "react-router-dom";
import {useAPI} from "./useAPI";
import {ItemType} from "../services/types";
import {WishlistItemResponse} from "../services/responses";
import {setPendingBooking} from "../services/pendingBooking";
import {toastMessage} from "../services/notify";

/**
 * Book / cancel booking for items rendered on a page. Guests are sent through the
 * sign-in flow first and the booking is completed automatically when they return.
 */
export const useBookingActions = (setItems: Dispatch<SetStateAction<ItemType[]>>) => {
  const api = useAPI();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const apply = (updated: WishlistItemResponse) => {
    setItems((current) => current.map((item) => item.uuid === updated.uuid
      ? {...item, booked: updated.booked, booked_by_me: updated.booked_by_me, booked_by: updated.booked_by}
      : item));
  };

  const book = async (uuid: string) => {
    if (!api.loggedIn) {
      setPendingBooking({itemUuid: uuid, returnTo: location.pathname});
      navigate('/signin');
      return;
    }

    const updated = await api.bookItem(uuid);
    if (!updated) {
      toast(toastMessage('Could not book this item. It may have just been taken.', 'error'));
      return;
    }

    apply(updated);
    toast(toastMessage('Booked! The owner will not get it twice now.', 'success'));
  };

  const unbook = async (uuid: string) => {
    const updated = await api.unbookItem(uuid);
    if (!updated) {
      toast(toastMessage('Could not cancel the booking.', 'error'));
      return;
    }

    apply(updated);
    toast(toastMessage('Booking cancelled.', 'success'));
  };

  return {book, unbook};
};
